/**
 * Owner: Masa Bayu
 * File: qxcircle-follow-graph.ts
 * Date:Time: 2026-09-18 11:11
 * Description: QXCircle follow lists — viewer handle from session, not @you_local.
 */

import { NotFoundException } from './_nestjs-common';
import { findProfileSeed } from './qxcircle-profile-seeds';
import { normalizeQxCircleAuthorHandle } from './qxcircle-service-helpers';
import type {
  QxCircleFeedPost,
  QxCircleFollowListKind,
  QxCircleFollowListPerson,
  QxCircleFollowListResponse,
} from './qxcircle.types';

/** Real follow edges only — populated by user actions, not mock graph. */
export const QXCIRCLE_SEED_FOLLOWING: Record<string, readonly string[]> = {};

function seedFollowingOf(authorHandle: string): string[] {
  return [...(QXCIRCLE_SEED_FOLLOWING[authorHandle] ?? [])];
}

function seedFollowersOf(authorHandle: string): string[] {
  return Object.entries(QXCIRCLE_SEED_FOLLOWING)
    .filter(([, following]) => following.includes(authorHandle))
    .map(([handle]) => handle);
}

export function qxCircleFollowHandles(
  authorHandle: string,
  kind: QxCircleFollowListKind,
  localFollows: Set<string>,
  viewerHandle: string | null = null,
): string[] {
  if (kind === 'following') {
    if (viewerHandle && authorHandle === viewerHandle) {
      return [...localFollows].sort((a, b) => a.localeCompare(b));
    }
    return seedFollowingOf(authorHandle).sort((a, b) => a.localeCompare(b));
  }
  const handles = seedFollowersOf(authorHandle);
  if (
    viewerHandle &&
    authorHandle !== viewerHandle &&
    localFollows.has(authorHandle)
  ) {
    handles.push(viewerHandle);
  }
  return [...new Set(handles)].sort((a, b) => a.localeCompare(b));
}

function resolveAuthorName(handle: string, posts: QxCircleFeedPost[]): string {
  const seed = findProfileSeed(handle);
  const post = posts.find((p) => p.authorHandle === handle);
  return seed?.authorName ?? post?.authorName ?? handle;
}

function requireKnownAuthor(
  handleRaw: string,
  posts: QxCircleFeedPost[],
): { authorHandle: string; authorName: string } {
  const authorHandle = normalizeQxCircleAuthorHandle(handleRaw);
  const seed = findProfileSeed(authorHandle);
  const authorPost = posts.find((p) => p.authorHandle === authorHandle);
  if (!seed && !authorPost) {
    throw new NotFoundException(`Author not found: ${authorHandle}`);
  }
  return {
    authorHandle,
    authorName: seed?.authorName ?? authorPost?.authorName ?? authorHandle,
  };
}

export function buildQxCircleFollowList(
  handleRaw: string,
  kind: QxCircleFollowListKind,
  posts: QxCircleFeedPost[],
  localFollows: Set<string>,
  blockedHandles: Set<string>,
  viewerHandle: string | null = null,
): QxCircleFollowListResponse {
  const { authorHandle, authorName } = requireKnownAuthor(handleRaw, posts);
  const items: QxCircleFollowListPerson[] = qxCircleFollowHandles(
    authorHandle,
    kind,
    localFollows,
    viewerHandle,
  )
    .filter((handle) => !blockedHandles.has(handle))
    .map((handle) => ({
      authorHandle: handle,
      authorName: resolveAuthorName(handle, posts),
      followedByLocal: localFollows.has(handle),
    }));
  return {
    world: 'qxcircle',
    slice: 'follow-list',
    kind,
    authorHandle,
    authorName,
    count: items.length,
    items,
  };
}
